import { useMemo } from "react";
import { useSelector } from "react-redux";
import { ReduxState } from "~/types/redux";

/**
 * Hook to check return visitor offer eligibility from Monarch rule
 * Uses only Redux state (from global ruleset saved by monarchCallback)
 * Does not make individual Monarch API calls
 *
 * Debug mode: Add ?debugRVO=true to URL to use test data with RVO offers
 */
export const useReturnVisitorOfferEligibility = (): boolean => {
  // Check for debug mode via URL parameter
  const isDebugMode =
    typeof window !== "undefined" && new URLSearchParams(window.location.search).get("debugRVO") === "true";

  // Get the value directly from Redux state (saved by monarchCallback from global ruleset)
  // Use equality check to prevent unnecessary re-renders
  const reduxData = useSelector(
    (store: ReduxState) => store.monarch?.returnVisitorOfferEligibility,
    (left, right) => {
      // Only re-render if the value actually changes
      if (left === right) return true;
      if (left === undefined || right === undefined) return false;
      // For primitive types, direct comparison
      if (typeof left !== "object" || typeof right !== "object") return left === right;
      // For objects, shallow comparison via JSON (simple approach)
      try {
        return JSON.stringify(left) === JSON.stringify(right);
      } catch {
        return false;
      }
    }
  );

  const isEligible = useMemo<boolean>(() => {
    // Return true if debug mode is enabled (for testing RVO features)
    if (isDebugMode) {
      return true;
    }

    // Use Redux state (from global ruleset)
    if (reduxData !== undefined) {
      if (typeof reduxData === "boolean") {
        return reduxData;
      }
      if (typeof reduxData === "object" && reduxData !== null && !Array.isArray(reduxData)) {
        const dataObj = reduxData as Record<string, unknown>;
        if (typeof dataObj.value === "boolean") {
          return dataObj.value;
        }
        if (typeof (dataObj.value as Record<string, unknown>)?.data === "boolean") {
          return (dataObj.value as Record<string, unknown>).data as boolean;
        }
        if (typeof dataObj.data === "boolean") {
          return dataObj.data;
        }
      }
    }

    // Default to false if not in Redux
    return false;
  }, [reduxData, isDebugMode]);

  return isEligible;
};

export default useReturnVisitorOfferEligibility;
